import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { formatDuration } from '../../utils/formatDuration';
import { deriveReturnTimeFraction } from '../../utils/tickCountdown';
import { resolveBindingConstraint } from '../../utils/exploreCapacity';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { StatusBanner } from '../shell/StatusBanner';
import { NumericInput } from '../strategy/NumericInput';
import type { ExploreShellProps } from '../../pages/Explore';

/**
 * SF-139: mobile card-based content for Explore. Purely presentational — success/failure
 * dispatch (toast vs. inline) lives in the page-level `handleSubmit` (Explore.tsx), not here.
 */
export function MobileExploreShell({
  province,
  acresToExplore,
  isSubmitting,
  handleInputChange,
  handleSubmit,
  maxExplorable,
  maxBySoldiers,
  maxByGold,
  maxByPool,
  kingdomExplorePool,
  isAtWar,
  soldierCostPerAcre,
  goldCostPerAcre,
  availableSoldiers,
  totalSoldierCost,
  totalGoldCost,
}: ExploreShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const exploration = province.territory.exploration;
  const bindingConstraint = resolveBindingConstraint(maxBySoldiers, maxByGold, maxByPool);

  // Same shared current-tick-elapsed fraction derivation as MobileBuildingsShell's "Under
  // Construction" card, applied to Exploration entries instead of Construction entries.
  const returnTimeFraction = deriveReturnTimeFraction(province.sync);

  const maxAtLimit = maxExplorable === 0 || acresToExplore === maxExplorable;

  return (
    <div className="space-y-3">
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold text-game-50 outline-none">
        Explore
      </h1>

      {isAtWar && (
        <StatusBanner
          tone="war"
          icon="⚔️"
          headline="Kingdom at war"
          detail="Exploration costs are doubled."
        />
      )}

      <GlanceCard title="Capacity">
        <MetricRow label="Max Explorable" value={formatNumber(maxExplorable)} unit="acres" weight="primary" />
        <div className="text-[11px] -mt-1.5" style={{ color: COLORS.text.tertiary }}>
          Limited by: {bindingConstraint}
        </div>
        <MetricRow label="Kingdom Pool" value={formatNumber(kingdomExplorePool)} unit="acres" weight="secondary" />
        <MetricRow label="Soldier Cost" value={soldierCostPerAcre} unit="soldiers / acre" weight="tertiary" />
        <div className="text-[10px] -mt-1.5" style={{ color: COLORS.text.tertiary }}>
          {formatNumber(availableSoldiers)} available
        </div>
        <MetricRow label="Gold Cost" value={formatNumber(goldCostPerAcre)} unit="gc / acre" weight="tertiary" />
      </GlanceCard>

      {exploration.length > 0 && (
        <GlanceCard title="In Transit">
          {exploration.map((exp, idx) => {
            const time = formatDuration(exp.ticksRemaining, {
              returnTimeFraction,
              tickIntervalMs: province.sync.tickIntervalMs,
            });
            return (
              <div key={`${exp.acres}-${idx}`} className="flex items-center justify-between gap-2 text-xs">
                <span style={{ color: COLORS.gold }}>{formatNumber(exp.acres)} acres</span>
                <span className="font-mono text-[11px]" style={{ color: COLORS.text.tertiary }}>{time}</span>
              </div>
            );
          })}
        </GlanceCard>
      )}

      <GlanceCard title="Send Expedition">
        <div className="flex items-center gap-3">
          <NumericInput
            value={acresToExplore}
            max={maxExplorable}
            onChange={handleInputChange}
            ariaLabel="Acres to explore"
            className="w-20"
          />
          <button
            onClick={() => handleInputChange(maxExplorable)}
            disabled={maxAtLimit}
            className="text-xs px-2.5 rounded-lg"
            style={{
              minHeight: 44,
              color: maxAtLimit ? COLORS.text.muted : COLORS.link,
              border: `1px solid ${maxAtLimit ? COLORS.text.muted : COLORS.link}`,
              background: 'none',
              opacity: maxAtLimit ? 0.5 : 1,
            }}
          >
            Max
          </button>
          <span className="text-xs" style={{ color: COLORS.text.tertiary }}>
            of {formatNumber(maxExplorable)} max
          </span>
        </div>

        <div className="flex items-center gap-4 text-xs" style={{ color: COLORS.text.tertiary }}>
          <span>{formatNumber(totalSoldierCost)} soldiers</span>
          <span>{formatNumber(totalGoldCost)} gc</span>
        </div>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting || acresToExplore < 1 || acresToExplore > maxExplorable}
          className="w-full text-center py-2.5 rounded-lg text-sm font-bold disabled:opacity-50"
          style={{
            minHeight: 44,
            backgroundColor: COLORS.btnPrimary,
            border: `1px solid ${COLORS.btnPrimaryHover}`,
            color: COLORS.text.primary,
          }}
        >
          {isSubmitting ? 'Sending…' : 'Send Expedition'}
        </button>
      </GlanceCard>
    </div>
  );
}
